import Link from 'next/link';
import styled, { css, keyframes } from 'styled-components';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';

import { Account } from 'layout/header/components/Account';
import { Accordion, IAccordionItem } from 'shared/components/Accordion';
import { Caret } from 'shared/icons/Caret';
import { CloseX } from 'shared/icons/CloseX';
import { GoatLogo } from 'shared/icons/GoatLogo';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { LOCALES } from 'localization/constants/configEnums';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { showOnMobileAndTabletOnly } from 'styles/utils/responsiveness';
import { SubMenuNavigation } from 'layout/header/components/SubMenuNavigation';
import { t } from 'localization/utils/t';
import { trackHeaderNavClick } from 'layout/header/utils/trackHeaderNavClick';
import { useOnRouteChangeComplete } from 'shared/hooks/useOnRouteChangeComplete';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { variableFont, variableFontRegular } from 'styles/theme/text';

const TP = 'layout.header.components.MobileNavigationPanel';

interface IMobileNavigationPanel {
  calendarLinks: ICMSBreadcrumb[];
  closeMobileNavMenu: () => void;
  headerItemsCount: number;
  headerLinks: ICMSBreadcrumb[];
  mobileNavPanelBodyRef: React.RefObject<HTMLDivElement>;
}

const trackHeaderTap = (category: string) => {
  sendTrackingEvent('HEADER_TAP', { header_category: category });
};

export const MobileNavigationPanel = ({
  calendarLinks,
  closeMobileNavMenu,
  headerItemsCount,
  headerLinks,
}: IMobileNavigationPanel) => {
  const { locale } = useRouter();
  const { isMenuVisible } = useStoreSelector((state) => state.pageLayout);
  const navBodyRef = useRef<HTMLDivElement>();
  useOnRouteChangeComplete(closeMobileNavMenu);

  const NAV_ACCORDION_ITEMS: IAccordionItem[] = [
    {
      title: t(`${TP}.shop`, 'Shop'),
      accordionItemKey: 'shop',
      content: (
        <SubMenuNavigation
          href="/search"
          menuItems={headerLinks}
          originalLabel="Shop"
          label={t(
            `${TP}.shopNumOfListings`,
            'Shop {numOfListings, plural, =1 {1 Listing} other {# Listings}}',
            {
              numOfListings: headerItemsCount,
            },
          )}
          specialNavLabel={t(`${TP}.shop`, 'Shop')}
        />
      ),
      trackClick: () => trackHeaderNavClick('shop'),
    },
    {
      title: t(`${TP}.timeline`, 'Timeline'),
      accordionItemKey: 'timeline',
      content: (
        <SubMenuNavigation
          href="/timeline"
          menuItems={calendarLinks}
          originalLabel="Timeline"
          label={t(`${TP}.timelineSneakerReleases`, 'Timeline: Sneaker Releases')}
        />
      ),
      trackClick: () => trackHeaderNavClick('timeline'),
    },
    {
      title: t(`${TP}.account`, 'Account'),
      accordionItemKey: 'account',
      content: <Account />,
      trackClick: () => trackHeaderNavClick('account'),
    },
  ];

  useEffect(() => {
    if (isMenuVisible) {
      disableBodyScroll(navBodyRef.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, [isMenuVisible]);

  return (
    <MobileNavigationPanel.Wrapper
      ref={navBodyRef}
      data-qa="mobile_navigation_panel"
      $isMenuVisible={isMenuVisible}
    >
      <div className="fixedHeader">
        <div className="navHeader">
          <Link href="/" passHref>
            <a
              aria-label={t(`${TP}.logoAltText`, 'GOAT logo')}
              data-qa="goat_logo_nav_panel"
              className="homeButtonLogo"
              onClick={() => trackHeaderTap('goatLogo')}
              onKeyDown={() => trackHeaderTap('goatLogo')}
              role="link"
              tabIndex={0}
            >
              <GoatLogo fill="white" height={8} width={36} />
            </a>
          </Link>
          <button
            className="closeButton"
            data-qa="close"
            onClick={closeMobileNavMenu}
            type="button"
          >
            <CloseX fill="white" size={10} />
          </button>
        </div>
        <Accordion items={NAV_ACCORDION_ITEMS} accordionKey="accordionNav" />
        {locale !== LOCALES.frFR && (
          <Link href="/editorial" passHref>
            <div
              className="editorialLink"
              onClick={() => trackHeaderTap('editorial')}
              onKeyDown={() => trackHeaderTap('editorial')}
              role="link"
              tabIndex={0}
            >
              <div>{t(`${TP}.editorial`, 'Editorial')}</div>
              <Caret
                color="white"
                direction="right"
                data-qa="accordion_caret"
                className="caret"
                size={13}
                strokeWidth={1}
              />
            </div>
          </Link>
        )}
      </div>
    </MobileNavigationPanel.Wrapper>
  );
};

const headerAnimation = keyframes`
  0% {
    transform: translateX(-40px);
  }
  100% {
    transform: translateX(0);
  }
`;

MobileNavigationPanel.Wrapper = styled.div<{ $isMenuVisible: boolean }>`
  ${({ $isMenuVisible }) =>
    showOnMobileAndTabletOnly`
    visibility: ${$isMenuVisible ? 'visible' : 'hidden'};
    opacity: ${$isMenuVisible ? '1' : '0'};
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background-color: ${({ theme }) => theme.colors.black};
    color: ${({ theme }) => theme.colors.white};
    z-index: ${({ theme }) => theme.zIndex.pageOverlay};
    transition: all 0.3s ease;
    overflow-y: auto;
  `};

  .navHeader {
    align-items: center;
    border-bottom: 0.3px solid ${({ theme }) => theme.colors.offBlack2};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 8px 16px 8px 12px;
    width: 100%;
  }

  .homeButtonLogo {
    padding: 10px 6px;
  }

  .closeButton {
    display: flex;
    padding: 10px 0px;

    :active {
      text-decoration: underline;
    }

    @media (hover: hover) {
      :hover {
        text-decoration: underline;
      }
    }
  }

  .fixedHeader {
    display: flex;
    flex-direction: column;
    align-items: center;
    ${variableFontRegular({ size: '11.5px', height: '11px' })};
    opacity: 0;
    width: 100%;

    ${({ $isMenuVisible }) =>
      $isMenuVisible &&
      css`
        animation: ${headerAnimation} 1s ease;
        opacity: 1;
        transition: opacity 0.5s linear;
      `}
  }

  .editorialLink {
    align-items: center;
    display: flex;
    cursor: pointer;
    flex-direction: row;
    justify-content: space-between;
    padding: 16px;
    width: 100%;

    ${variableFont({ size: '12px', height: '16px', fontWeight: '700' })}
  }
`;
